import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Search, RefreshCw, Activity, Camera, CameraOff, ScanLine, Users } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("id-ID");
}

export default function Scan() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [scanValue, setScanValue] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanMessage, setScanMessage] = useState("");
  const [scanError, setScanError] = useState("");
  const [selectedMemberName, setSelectedMemberName] = useState("");
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const scanInputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectIntervalRef = useRef(null);
  const lastDetectedRef = useRef({ value: "", at: 0 });

  useEffect(() => {
    fetchLogs();
  }, []);

  useEffect(() => {
    scanInputRef.current?.focus();
  }, []);

  useEffect(() => {
    return () => {
      if (detectIntervalRef.current) {
        window.clearInterval(detectIntervalRef.current);
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/checkins?limit=500`, { withCredentials: true });
      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch check-in logs:", error);
      alert(error.response?.data?.detail || "Gagal mengambil log activity member.");
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return logs;
    return logs.filter((item) => {
      const haystack = [
        String(item.member_name || ""),
        String(item.member_merchant_name || ""),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [logs, query]);

  const uniqueMemberLogs = useMemo(() => {
    const map = new Map();
    for (const item of filteredLogs) {
      const key = String(item.member_name || "").trim().toLowerCase();
      if (!key) continue;
      if (!map.has(key)) {
        map.set(key, item);
      }
    }
    return Array.from(map.values());
  }, [filteredLogs]);

  const selectedMemberLogs = useMemo(() => {
    const normalized = String(selectedMemberName || "").trim().toLowerCase();
    if (!normalized) return [];
    return logs.filter((item) => String(item.member_name || "").trim().toLowerCase() === normalized);
  }, [logs, selectedMemberName]);

  const submitScanPayload = async (rawPayload) => {
    const payload = String(rawPayload || "").trim();
    if (!payload) return;

    try {
      setScanning(true);
      setScanError("");
      setCameraError("");
      const { data } = await axios.post(
        `${API}/checkins/scan`,
        { qr_payload: payload },
        { withCredentials: true }
      );
      if (data?.checkin) {
        setLogs((prev) => [data.checkin, ...prev]);
      } else {
        await fetchLogs();
      }
      setScanMessage(data?.message || "Scan berhasil, log activity tersimpan.");
      setScanValue("");
    } catch (error) {
      console.error("Failed to scan member QR:", error);
      setScanMessage("");
      const message = error.response?.data?.detail || "Scan gagal diproses.";
      setScanError(message);
      if (cameraActive) {
        setCameraError(message);
      }
    } finally {
      setScanning(false);
      window.setTimeout(() => scanInputRef.current?.focus(), 50);
    }
  };

  const handleScanSubmit = async (e) => {
    e.preventDefault();
    await submitScanPayload(scanValue);
  };

  const handleScanInputKeyDown = (e) => {
    if (e.key === "Tab" && scanValue.trim()) {
      e.preventDefault();
      handleScanSubmit(e);
    }
  };

  const stopCameraScan = () => {
    if (detectIntervalRef.current) {
      window.clearInterval(detectIntervalRef.current);
      detectIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const startCameraScan = async () => {
    setCameraError("");
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("Browser tidak mendukung akses kamera.");
      return;
    }
    if (typeof window.BarcodeDetector === "undefined") {
      setCameraError("Browser belum mendukung scan barcode via kamera. Gunakan Chrome/Edge terbaru.");
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);

      const supportedFormats = await window.BarcodeDetector.getSupportedFormats();
      const preferredFormats = ["qr_code", "code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e"];
      const activeFormats = preferredFormats.filter((fmt) => supportedFormats.includes(fmt));
      const detector = new window.BarcodeDetector({
        formats: activeFormats.length > 0 ? activeFormats : undefined,
      });

      detectIntervalRef.current = window.setInterval(async () => {
        if (!videoRef.current || scanning) return;
        try {
          const barcodes = await detector.detect(videoRef.current);
          if (!Array.isArray(barcodes) || barcodes.length === 0) return;

          const rawValue = String(barcodes[0]?.rawValue || "").trim();
          if (!rawValue) return;

          const now = Date.now();
          if (lastDetectedRef.current.value === rawValue && now - lastDetectedRef.current.at < 3000) return;
          lastDetectedRef.current = { value: rawValue, at: now };

          setScanValue(rawValue);
          await submitScanPayload(rawValue);
        } catch (_) {
          // Ignore intermittent detector errors from video frames.
        }
      }, 500);
    } catch (error) {
      console.error("Failed to start camera scan:", error);
      setCameraError("Gagal menyalakan kamera. Cek izin kamera di browser.");
      stopCameraScan();
    }
  };

  return (
    <DashboardLayout>
      <Header title="Scan" />
      <div className="space-y-5 p-8" data-testid="scan-page">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
          <div className="rounded-sm border border-white/10 bg-[linear-gradient(135deg,rgba(255,255,255,0.035),rgba(9,9,11,0.88))] p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#F3D77A]">
              <Activity className="h-3.5 w-3.5" />
              Activity Log
            </div>
            <h2 className="text-2xl font-semibold tracking-tight text-white">Absensi Member</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-zinc-500">
              Scan QR atau barcode member untuk mencatat absen/check-in. Riwayat terbaru akan muncul otomatis di tabel log.
            </p>
          </div>
          <div className="rounded-sm border border-[#D4AF37]/25 bg-[linear-gradient(135deg,rgba(212,175,55,0.14),rgba(9,9,11,0.88))] p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Member Log</p>
              <Users className="h-5 w-5 text-[#D4AF37]" />
            </div>
            <p className="mt-4 text-4xl font-black tracking-tight text-white">{uniqueMemberLogs.length}</p>
            <p className="mt-2 text-xs text-zinc-500">Member unik yang pernah absen.</p>
          </div>
        </div>

        <section className="rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/[0.035] p-4">
          <div className="mb-3 flex items-center gap-2">
            <ScanLine className="h-4 w-4 text-[#F3D77A]" />
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#F3D77A]">Scan Absen Member</p>
          </div>
        <div className="flex flex-col gap-3 xl:flex-row">
          <form onSubmit={handleScanSubmit} className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
            <Input
              ref={scanInputRef}
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={handleScanInputKeyDown}
              placeholder="Scan barcode / QR member di sini..."
              className="h-11 border-[#D4AF37]/35 bg-zinc-950/70 text-white placeholder:text-zinc-500"
            />
            <Button
              type="submit"
              disabled={!scanValue.trim() || scanning}
              className="h-11 rounded-sm bg-[#D4AF37] px-6 font-semibold text-black hover:bg-[#C59A27]"
            >
              {scanning ? "Scanning..." : "Scan"}
            </Button>
          </form>
          <Button
            type="button"
            variant="outline"
            onClick={cameraActive ? stopCameraScan : startCameraScan}
            className="h-11 rounded-sm border-white/15 bg-white/[0.03] px-5 text-white hover:bg-white/[0.06]"
          >
            {cameraActive ? (
              <>
                <CameraOff className="mr-2 h-4 w-4" />
                Stop Kamera
              </>
            ) : (
              <>
                <Camera className="mr-2 h-4 w-4" />
                Scan Kamera
              </>
            )}
          </Button>
        </div>
          <p className="mt-3 text-xs text-zinc-500">Tips: scanner barcode biasanya otomatis menekan Enter setelah membaca kode.</p>
        </section>

        {(cameraActive || cameraError) && (
          <div className="mb-4 rounded-sm border border-white/10 bg-black/25 p-3">
            {cameraActive ? (
              <video ref={videoRef} className="h-64 w-full rounded-sm bg-black object-cover" muted playsInline />
            ) : null}
            {cameraError ? <p className="mt-2 text-xs text-red-300">{cameraError}</p> : null}
          </div>
        )}

        {(scanMessage || scanError) && (
          <div
            className={`mb-4 rounded-sm border px-3 py-2 text-sm ${
              scanError
                ? "border-red-400/25 bg-red-500/10 text-red-200"
                : "border-emerald-400/25 bg-emerald-500/10 text-emerald-200"
            }`}
          >
            {scanError || scanMessage}
          </div>
        )}

        <section className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900/45">
          <div className="flex flex-col justify-between gap-3 border-b border-white/10 px-5 py-4 lg:flex-row lg:items-center">
            <div>
              <h3 className="text-base font-semibold text-white">Riwayat Activity Member</h3>
              <p className="mt-1 text-xs text-zinc-500">Klik nama member untuk melihat riwayat scan lengkap.</p>
            </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative w-full sm:w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama member..."
              className="h-10 border-white/10 bg-zinc-950/60 pl-9 text-white"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={fetchLogs}
            className="h-10 rounded-sm border-white/15 bg-white/[0.03] text-white hover:bg-white/[0.06]"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          </div>
        </div>

        {selectedMemberName && (
          <div className="m-4 rounded-sm border border-[#D4AF37]/25 bg-[#D4AF37]/10 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-white">{selectedMemberName}</p>
                <p className="text-xs text-zinc-300">
                  Total {selectedMemberLogs.length} log activity.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setSelectedMemberName("")}
                className="h-8 rounded-sm border-white/20 bg-white/[0.03] px-3 text-xs text-white hover:bg-white/[0.08]"
              >
                Tutup
              </Button>
            </div>
            <div className="mt-3 max-h-40 space-y-1 overflow-y-auto">
              {selectedMemberLogs.map((log, idx) => (
                <div key={log.id || `${selectedMemberName}-${idx}`} className="rounded-sm bg-black/20 px-2.5 py-1.5 text-xs text-zinc-200">
                  {formatDateTime(log.checked_in_at)}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="max-h-[56vh] overflow-auto">
          <Table>
            <TableHeader className="bg-white/[0.02]">
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead>No</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Terdaftar Di</TableHead>
                <TableHead>Waktu Activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : uniqueMemberLogs.length > 0 ? (
                uniqueMemberLogs.map((item, index) => (
                  <TableRow
                    key={item.id || `${item.member_id || "member"}-${index}`}
                    className="border-white/5 hover:bg-white/[0.03] odd:bg-black/10"
                  >
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => setSelectedMemberName(item.member_name || "")}
                        className="font-medium text-white underline-offset-2 transition-colors hover:text-[#F3D77A] hover:underline"
                      >
                        {item.member_name || "-"}
                      </button>
                    </TableCell>
                    <TableCell className="text-zinc-300">{item.member_merchant_name || "-"}</TableCell>
                    <TableCell className="text-zinc-300">{formatDateTime(item.checked_in_at)}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="py-8 text-center text-sm text-zinc-500">
                    Belum ada log activity member.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        </section>
      </div>
    </DashboardLayout>
  );
}
